import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase';
import { formatMinutesToHumanReadable } from '../timeUtils';

export const DEFAULT_MEAL_BREAK_DURATION = 30;
export const DEFAULT_REST_BREAK_DURATION = 10;

export const fetchLaborLawsForVenue = async (venueId) => {
  const fetchLaborLawsForVenue = httpsCallable(functions, 'fetchLaborLawsForVenue');
  const result = await fetchLaborLawsForVenue({ vid: venueId });
  return result.data;
};

/**
 * Fetches labor laws for display using the existing backend function
 * @param {string} venueId - The venue ID to get labor laws for
 * @returns {Promise<Object>} Object containing state, stateName, and laws array
 */
export const fetchLaborLawsForDisplay = async (venueId) => {
  try {
    if (!venueId) {
      throw new Error('Venue ID is required');
    }

    const result = await fetchLaborLawsForVenue(venueId);

    // Backend returns { state, stateName, laws } structure
    const { state, stateName, laws: laborLaws } = result;

    // Validate that we have state information
    if (!state || !stateName) {
      throw new Error(
        'Unable to determine venue state from address. Please ensure the venue has a valid address with state information.'
      );
    }

    // Convert the technical backend data to display format
    const displayLaws = convertLaborLawsToDisplay(laborLaws);

    return {
      state,
      stateName,
      laws: displayLaws,
    };
  } catch (error) {
    console.error('Error fetching labor laws:', error);
    throw new Error(`Failed to fetch labor laws: ${error.message}`);
  }
};

// Helper to check for non-null, finite numbers
const isValidFiniteNumber = (value) => {
  return value != null && Number.isFinite(value);
};

// Keep the conversion function for display formatting
const convertLaborLawsToDisplay = (laborLaws) => {
  const laws = [];

  // Meal breaks - show ALL meal break related fields
  if (isValidFiniteNumber(laborLaws.meal_break_required_after)) {
    laws.push({
      title: 'Meal breaks',
      description: `${laborLaws.meal_break_duration || 30}-minute meal break for shifts over ${formatMinutesToHumanReadable(laborLaws.meal_break_required_after)}`,
    });
  }

  // Second meal break
  if (isValidFiniteNumber(laborLaws.second_meal_break_required_after)) {
    laws.push({
      title: 'Second meal break',
      description: `Second meal break required for shifts over ${formatMinutesToHumanReadable(laborLaws.second_meal_break_required_after)}`,
    });
  }

  // Third meal break - handle Infinity gracefully
  if (laborLaws.third_meal_break_required_after != null) {
    if (Number.isFinite(laborLaws.third_meal_break_required_after)) {
      laws.push({
        title: 'Third meal break',
        description: `Third meal break required for shifts over ${formatMinutesToHumanReadable(laborLaws.third_meal_break_required_after)}`,
      });
    } else {
      // Handle Infinity case as suggested by your lead
      laws.push({
        title: 'Third meal break',
        description: 'No time set',
      });
    }
  }

  // Meal break minimum time from shift start
  if (
    isValidFiniteNumber(laborLaws.meal_break_min_time_from_shift_start) &&
    laborLaws.meal_break_min_time_from_shift_start > 0
  ) {
    laws.push({
      title: 'Meal break timing',
      description: `Meal break cannot start within ${formatMinutesToHumanReadable(laborLaws.meal_break_min_time_from_shift_start)} of the beginning of the shift`,
    });
  }

  // Rest breaks
  if (isValidFiniteNumber(laborLaws.rest_break_interval)) {
    laws.push({
      title: 'Rest breaks',
      description: `${laborLaws.rest_break_duration || 10}-minute paid rest break per ${formatMinutesToHumanReadable(laborLaws.rest_break_interval)} worked`,
    });
  }

  // Rest break applies to major fractions
  if (laborLaws.rest_break_applies_to_major_fractions === true) {
    laws.push({
      title: 'Rest break rules',
      description: 'Rest breaks apply to major fractions of work periods',
    });
  }

  // Maximum shift duration to waive first meal break
  if (isValidFiniteNumber(laborLaws.max_shift_duration_to_waive_first_meal_break)) {
    laws.push({
      title: 'First meal break waiver',
      description: `First meal break can be waived for shifts under ${formatMinutesToHumanReadable(laborLaws.max_shift_duration_to_waive_first_meal_break)}`,
    });
  } else if (laborLaws.max_shift_duration_to_waive_first_meal_break === Infinity) {
    laws.push({
      title: 'First meal break waiver',
      description: 'First meal break cannot be waived',
    });
  }

  // Maximum shift duration to waive second meal break
  if (isValidFiniteNumber(laborLaws.max_shift_duration_to_waive_second_meal_break)) {
    laws.push({
      title: 'Second meal break waiver',
      description: `Second meal break can be waived for shifts under ${formatMinutesToHumanReadable(laborLaws.max_shift_duration_to_waive_second_meal_break)}`,
    });
  } else if (laborLaws.max_shift_duration_to_waive_second_meal_break === Infinity) {
    laws.push({
      title: 'Second meal break waiver',
      description: 'Second meal break cannot be waived',
    });
  }

  // Waived rest breaks allowed
  if (laborLaws.waived_rest_breaks_allowed !== undefined) {
    laws.push({
      title: 'Rest break waiver',
      description: laborLaws.waived_rest_breaks_allowed
        ? 'Rest breaks can be waived by employees'
        : 'Rest breaks cannot be waived',
    });
  }

  // Second meal waivable if first waived
  if (laborLaws.second_meal_waivable_if_first_waived !== undefined) {
    laws.push({
      title: 'Second meal break waiver rule',
      description: laborLaws.second_meal_waivable_if_first_waived
        ? 'Second meal break can be waived if first was waived'
        : 'Second meal break waiver independent of first meal break',
    });
  }

  // Add any additional laws that the backend provides
  if (laborLaws.additional_laws && Array.isArray(laborLaws.additional_laws)) {
    laws.push(...laborLaws.additional_laws);
  }

  return laws;
};
